import { useTranslate } from '@tolgee/react';
import { getNavigation, getPartners, NavigationProps } from '@amf/shared/utils/navigation';
import Container from '@amf/shared/components/layout/Container';
import { GetServerSideProps } from 'next';
import React, { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { isArray } from '@apollo/client/cache/inmemory/helpers';
import { getLanguageLocale } from '@amf/shared/utils/languages';
import { sdk } from '@amf/shared/lib/graphQlClient';
import {
  SeasonFragment,
  SeasonLeagueFragment,
  TeamDetailItemFragment,
  TopPlayerFragment,
  TopPlayersFragment,
} from '@amf/shared/types/graphql';
import SeasonHeader from '@amf/shared/components/season/SeasonHeader';
import Menu from '@amf/shared/components/menu/Menu';
import useTeamPlayers from '@amf/shared/data/hooks/useTeamPlayers';
import { findTeamDefaults } from '@amf/shared/data/Team';
import Loader from '@amf/shared/components/utils/Loader';
import SelectField from '@amf/shared/components/form/SelectField';

type Props = {
  team: TeamDetailItemFragment;
  seasons: SeasonFragment[];
  prefixes: { player: string };
};

export type TeamTopPlayersPageProps = NavigationProps & {
  team: TeamDetailItemFragment;
  seasons: SeasonFragment[];
};

function TeamTopPlayersPage({ team, seasons, prefixes }: Props) {
  const { t } = useTranslate();

  const defaultValues = useMemo(() => {
    const { league, season } = findTeamDefaults(team, seasons);
    return {
      league: league ?? seasons[0]?.leagues[0],
      season: season ?? seasons[0],
    };
  }, [seasons, team]);

  const [season, setSeason] = useState<SeasonFragment>(defaultValues.season);
  const [league, setLeague] = useState<SeasonLeagueFragment | undefined>(defaultValues.league);

  useEffect(() => setLeague(season.leagues[0]), [season]);

  const { players, loading } = useTeamPlayers(league?.id, season?.id, team.id, 1000);

  const [type, setType] = useState<keyof TopPlayersFragment>('mostGoals');

  const data = useMemo(() => {
    if (!players) return [];
    const data = players[type];
    if (isArray(data)) return data as TopPlayerFragment[];
    return [];
  }, [players, type]);

  return (
    <>
      <SeasonHeader
        title={t('stats.team.title', { team: team.name })}
        seasons={seasons}
        selected={season}
        onSeasonChange={season => setSeason(season)}
      />
      {league && (
        <>
          <Menu<SeasonLeagueFragment>
            scrollable
            onChange={({ value }) => setLeague(value)}
            selected={{ id: league.id, title: league.name, value: league }}
            items={season.leagues.map(league => ({
              id: league.id,
              title: league.name,
              value: league,
            }))}
          />
          <Container>
            <div className='Filter'>
              <div className='Filter__form'>
                <SelectField
                  className='Filter__field'
                  label={t('stats.team.type')}
                  value={type}
                  onChange={e => setType(e.target.value as keyof TopPlayersFragment)}
                >
                  <option value='mostGoals'>{t('team.playerStats.mostGoals')}</option>
                  <option value='mostAssists'>{t('team.playerStats.mostAssists')}</option>
                  <option value='mostPoints'>{t('team.playerStats.mostPoints')}</option>
                  <option value='mostMatches'>{t('team.playerStats.mostMatches')}</option>
                  <option value='mostYellowCards'>{t('team.playerStats.mostYellowCards')}</option>
                  <option value='mostRedCards'>{t('team.playerStats.mostRedCards')}</option>
                  <option value='mostGoalsInOneMatch'>
                    {t('team.playerStats.mostGoalsInOneMatch')}
                  </option>
                  <option value='mostAssistsInOneMatch'>
                    {t('team.playerStats.mostAssistsInOneMatch')}
                  </option>
                  <option value='mostPointsInOneMatch'>
                    {t('team.playerStats.mostPointsInOneMatch')}
                  </option>
                </SelectField>
              </div>
            </div>
            {loading && <Loader size='small'>{t('loading')}</Loader>}
            {!loading && data.length > 0 && (
              <div className='SeasonPlayerStatsTable'>
                <table className='SeasonPlayerStatsTable__table'>
                  <thead>
                    <tr>
                      <th>&nbsp;</th>
                      <th>{t('stats.grid.player')}</th>
                      <th>{t(`stats.grid.playerStats.${type}`)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((player: TopPlayerFragment, order) => (
                      <tr key={player.id}>
                        <td width={16}>
                          <div className='SeasonPlayerStatsTable__order'>{order + 1}</div>
                        </td>
                        <td>
                          <Link href={`${prefixes.player}/${player.id}`}>
                            <a className='SeasonPlayerStatsTable__cell SeasonPlayerStatsTable__cell--horizontal'>
                              {player.photoUrl && (
                                // eslint-disable-next-line @next/next/no-img-element
                                <img
                                  className='SeasonPlayerStatsTable__avatar'
                                  src={player.photoUrl}
                                  alt={player.fullName}
                                />
                              )}
                              {!player.photoUrl && (
                                <div className='SeasonPlayerStatsTable__avatar' />
                              )}
                              <strong>{player.fullName}</strong>
                            </a>
                          </Link>
                        </td>
                        <td>
                          <div className='SeasonPlayerStatsTable__cell'>
                            <div>{player.value}</div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Container>
        </>
      )}
    </>
  );
}

export default TeamTopPlayersPage;

export const getServerSideProps =
  (): GetServerSideProps<TeamTopPlayersPageProps, { team: string }> =>
  async ({ locale, params, res }) => {
    if (!params?.team) return { notFound: true };
    res.setHeader('Cache-Control', 'max-age=0, s-maxage=10');
    const { seasons } = await sdk.SeasonList({ union: process.env.NEXT_PUBLIC_UNION });
    if (seasons.length === 0) return { notFound: true };
    const { team } = await sdk.TeamDetail({
      union: process.env.NEXT_PUBLIC_UNION,
      id: params.team,
    });
    if (!team) return { notFound: true };

    return {
      props: {
        team,
        seasons,
        navigation: await getNavigation(getLanguageLocale(locale!)),
        partners: await getPartners(),
      },
    };
  };
